import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import type {
  ApiAcceptedLanguage,
  ApiComponentType,
  ApiError,
  ApiNabanAuthenticationModes,
  ApiNabanDeeds,
  NabanApi,
} from '@izimi/api';
import { FIFTEEN_MINUTES_MS, THIRTY_MINUTES_MS } from '@izimi/utils';

import { useInvalidateDocumentsVaultListCache } from '../documents';
import { nabanQueryKeys } from './cache';

const STALE_30_MINUTES = 1800000;

const createNabanModule = ({ nabanApi }: { nabanApi: NabanApi }) => {
  const useGetNabanAuthenticationModes = () => {
    const { data, isLoading, error, refetch } = useQuery<ApiNabanAuthenticationModes, ApiError>({
      queryKey: nabanQueryKeys.getNabanAuthenticationModes(),
      queryFn: () => nabanApi.getNabanAuthenticationModes(),
      staleTime: THIRTY_MINUTES_MS,
    });

    const hasNoHeirsAndNoCompanies =
      data?.heirAuthentications?.length === 0 && data?.companyAuthentications?.length === 0;

    const hasNoCitizenAuthenticationModeToken =
      !data?.citizenNrnChainServiceDown &&
      data?.citizenAuthentication?.authenticationModeToken == null;

    const hasOnlyCitizenModeToken =
      hasNoHeirsAndNoCompanies && data?.citizenAuthentication?.authenticationModeToken;

    return {
      nabanAuthenticationModes: data,
      hasNoHeirsAndNoCompanies,
      hasNoCitizenAuthenticationModeToken,
      hasOnlyCitizenModeToken,
      error,
      isLoading,
      refetch,
    };
  };

  const useGetNabanDeeds = ({
    language,
    authenticationModeToken,
    pageSize = 10,
  }: {
    language: ApiAcceptedLanguage;
    authenticationModeToken?: string;
    pageSize?: 5 | 10 | 20 | 30;
  }) => {
    const {
      data,
      isLoading,
      error,
      refetch,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      isSuccess,
    } = useInfiniteQuery<ApiNabanDeeds, ApiError>({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryKey: nabanQueryKeys.getNabanDeeds(authenticationModeToken!, language),
      initialPageParam: 0,
      queryFn: ({ pageParam = 0 }) =>
        nabanApi.getNabanDeeds({
          offset: Number(pageParam),
          limit: pageSize,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          authenticationModeToken: authenticationModeToken!,
          language,
        }),

      getNextPageParam: (lastPage, allPages) => {
        const totalFetchedItems = allPages.flatMap((page) => page.deedPage.list).length;

        return totalFetchedItems < lastPage.deedPage.size ? totalFetchedItems : undefined;
      },
      enabled: Boolean(authenticationModeToken),
      staleTime: STALE_30_MINUTES,
    });

    return {
      deeds: data?.pages.flatMap((page) => page.deedPage.list) ?? [],
      error,
      isLoading,
      refetch,
      fetchNextPage,
      isFetchingNextPage,
      isSuccess,
      hasNextPage,
    };
  };

  const useGetNabanDeed = ({
    authenticationModeToken,
    napId,
    language,
  }: {
    language: ApiAcceptedLanguage;
    authenticationModeToken?: string;
    napId?: string;
  }) => {
    const { data, isLoading, error, refetch } = useQuery({
      // Query is disabled if napId is undefined
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryKey: nabanQueryKeys.getNabanDeed(authenticationModeToken!, napId!, language),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryFn: () =>
        nabanApi.getNabanDeed({
          authenticationModeToken: authenticationModeToken!,
          napId: napId!,
          language,
        }),
      enabled: Boolean(napId && authenticationModeToken),
      staleTime: FIFTEEN_MINUTES_MS,
    });

    return {
      deed: data,
      error,
      isLoading,
      refetch,
    };
  };

  const useCopyNabanDeedDocuments = () => {
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const [pendingDocumentKeys, setPendingDocumentKeys] = useState<Array<string>>([]);

    const { mutateAsync, isPending, error } = useMutation<
      undefined,
      ApiError,
      ApiComponentType<'NabanCopyRequest'> & { documentKey?: string }
    >({
      mutationFn: async ({ documentKey, ...params }) => {
        if (documentKey) {
          setPendingDocumentKeys((prev) => [...prev, documentKey]);
        }

        await nabanApi.copyNabanDeedDocument(params);
      },
      onSuccess: () => invalidateDocumentsVaultListCache(),
      onSettled: (_, __, { documentKey }) => {
        setPendingDocumentKeys((prev) => prev.filter((key) => key !== documentKey));
      },
    });

    return {
      copyNabanDeedDocuments: mutateAsync,
      pendingDocumentKeys,
      error,
      isPending,
    };
  };

  const useDownloadNabanDeedDocument = () => {
    const { mutateAsync, isPending, error } = useMutation<
      Blob,
      ApiError,
      Parameters<typeof nabanApi.downloadNabanDeedDocument>[0]
    >({
      mutationFn: (params) => nabanApi.downloadNabanDeedDocument(params),
    });

    return {
      downloadNabanDeedDocument: mutateAsync,
      error,
      isPending,
    };
  };

  return {
    useGetNabanAuthenticationModes,
    useGetNabanDeeds,
    useGetNabanDeed,
    useCopyNabanDeedDocuments,
    useDownloadNabanDeedDocument,
  };
};

export default createNabanModule;
